// ensureAllowance.js
import { erc20Abi, parseUnits } from "viem";

/**
 * Ensures ERC-20 allowance >= amount.
 * Uses the safe pattern: approve(0) -> approve(amount), with simulation first.
 * @returns {Promise<boolean>} true if allowance is sufficient / set successfully
 */
export default async function ensureAllowance({
  account, // "0xabc..."
  publicClient, // from wagmi's usePublicClient()
  walletClient, // from wagmi's useWalletClient()
  token, // token address
  spender, // contract that will spend the tokens
  amount, // bigint (e.g., parseUnits("100", 18))
  // Optional: when true (default) approve 1 crore (10,000,000) tokens allowance
  // only when the requested `amount` is greater than current allowance.
  useUnlimited = true,
}) {
  const current = await publicClient.readContract({
    abi: erc20Abi,
    address: token,
    functionName: "allowance",
    args: [account, spender],
  });

  if (current >= amount) return true;

  // Use 1 crore (10,000,000) tokens with 18 decimals as allowance
  const ONE_CRORE_ALLOWANCE = parseUnits("10000000", 18);

  // Some tokens require approve(0) before a new non-zero approval
  if (current > 0n) {
    try {
      const { request } = await publicClient.simulateContract({
        abi: erc20Abi,
        address: token,
        functionName: "approve",
        args: [spender, 0n],
        account,
        value: 0n,
      });
      const hash0 = await walletClient.writeContract(request);
      await publicClient.waitForTransactionReceipt({ hash: hash0 });
    } catch (err) {
      // If zero-approval fails, surface false so caller can handle it
      console.log("err", err);
      return false;
    }
  }

  // Decide whether to set 1 crore allowance or only the exact amount.
  const allowanceToSet = useUnlimited ? ONE_CRORE_ALLOWANCE : amount;

  try {
    const { request } = await publicClient.simulateContract({
      abi: erc20Abi,
      address: token,
      functionName: "approve",
      args: [spender, allowanceToSet],
      account,
      value: 0n,
    });
    const hash1 = await walletClient.writeContract(request);
    const receipt = await publicClient.waitForTransactionReceipt({
      hash: hash1,
    });

    return receipt.status === "success";
  } catch (err) {
    console.log("err", err);
    return false;
  }
}
